import { useCallback, useEffect, useState } from 'react';

import { useUser } from 'global/UserContext';
import { useToast } from 'hooks/useToast';
import { apiOrchestrator } from 'services/apiService';

interface SocialNetwork {
  id?: number;
  social_network_type: string;
  social_network_username: string;
}

// Evento personalizado para notificar atualizações
const USER_SOCIAL_NETWORKS_UPDATED = 'userSocialNetworksUpdated';

export const useUserSocialNetworks = () => {
  const { user } = useUser();
  const { toastError, toastSuccess } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [socialNetworks, setSocialNetworks] = useState<SocialNetwork[]>([]);
  const [networkTypes, setNetworkTypes] = useState<string[]>([]);

  const handleApiError = useCallback(
    (error: any, defaultMessage: string, operation: string) => {
      const errorMessage = error.response?.data?.message || defaultMessage;
      setError(errorMessage);
      toastError(`${operation}: ${errorMessage}`);
    },
    [toastError]
  );

  const fetchNetworkTypes = useCallback(async () => {
    if (!user?.id) {
      handleApiError({}, 'Usuário não autenticado', 'Busca de tipos de redes');
      return;
    }

    try {
      const { data } = await apiOrchestrator.get('/user-social-networks/types');
      if (!data || !Array.isArray(data)) {
        throw new Error('Formato de resposta inválido');
      }
      setNetworkTypes(data);
      setError(null);
    } catch (error) {
      handleApiError(
        error,
        'Falha ao carregar tipos de redes sociais disponíveis',
        'Busca de tipos de redes'
      );
    }
  }, [user?.id, handleApiError]);

  const fetchSocialNetworks = useCallback(async () => {
    if (!user?.id) {
      handleApiError({}, 'Usuário não autenticado', 'Busca de redes sociais');
      return;
    }

    setLoading(true);
    try {
      const { data } = await apiOrchestrator.get(`/user-social-networks/?user_id=${user.id}`);
      if (!data || !Array.isArray(data)) {
        throw new Error('Formato de resposta inválido');
      }
      setSocialNetworks(data);
      setError(null);
    } catch (error) {
      handleApiError(error, 'Falha ao carregar suas redes sociais', 'Busca de redes sociais');
    } finally {
      setLoading(false);
    }
  }, [user?.id, handleApiError]);

  // Função para notificar atualizações
  const notifyUpdate = useCallback(() => {
    if (!user?.id) return;
    const event = new CustomEvent(USER_SOCIAL_NETWORKS_UPDATED, {
      detail: { userId: user.id },
    });
    window.dispatchEvent(event);
  }, [user?.id]);

  const getUsernameByType = useCallback(
    (type: string): SocialNetwork | undefined => {
      if (!type) {
        handleApiError({}, 'Tipo de rede social não especificado', 'Busca de usuário');
        return undefined;
      }
      return socialNetworks.find(net => net.social_network_type === type);
    },
    [socialNetworks, handleApiError]
  );

  const upsertSocialNetwork = async (socialNetwork: SocialNetwork) => {
    if (!user?.id) {
      handleApiError({}, 'Usuário não autenticado', 'Atualização de rede social');
      return;
    }

    if (!socialNetwork.social_network_type || !socialNetwork.social_network_username) {
      handleApiError({}, 'Dados da rede social incompletos', 'Validação de dados');
      return;
    }

    const existingNetwork = socialNetworks.find(
      net => net.social_network_type === socialNetwork.social_network_type
    );

    try {
      if (existingNetwork) {
        await apiOrchestrator.patch(`/user-social-networks/${user.id}`, {
          id: existingNetwork.id,
          ...socialNetwork,
        });
      } else {
        await apiOrchestrator.post(`/user-social-networks/${user.id}`, socialNetwork);
      }

      // Atualiza a lista de redes sociais
      await fetchSocialNetworks();

      // Notifica que houve uma atualização
      notifyUpdate();

      toastSuccess(
        `Rede social ${socialNetwork.social_network_type} ${existingNetwork ? 'atualizada' : 'adicionada'} com sucesso!`
      );
      setError(null);
    } catch (error) {
      handleApiError(
        error,
        `Falha ao ${existingNetwork ? 'atualizar' : 'adicionar'} rede social ${socialNetwork.social_network_type}`,
        'Atualização de rede social'
      );
    }
  };

  const deleteSocialNetwork = async (networkId: number) => {
    if (!user?.id) {
      handleApiError({}, 'Usuário não autenticado', 'Remoção de rede social');
      return;
    }

    if (!networkId) {
      handleApiError({}, 'ID da rede social não fornecido', 'Validação de dados');
      return;
    }

    const networkToDelete = socialNetworks.find(net => net.id === networkId);
    if (!networkToDelete) {
      handleApiError({}, 'Rede social não encontrada', 'Validação de dados');
      return;
    }

    try {
      await apiOrchestrator.delete(`/user-social-networks/${user.id}/${networkId}`);

      // Atualiza a lista de redes sociais
      await fetchSocialNetworks();

      // Notifica que houve uma atualização
      notifyUpdate();

      toastSuccess(`Rede social ${networkToDelete.social_network_type} removida com sucesso!`);
      setError(null);
    } catch (error) {
      handleApiError(
        error,
        `Falha ao remover rede social ${networkToDelete.social_network_type}`,
        'Remoção de rede social'
      );
    }
  };

  // Hook para escutar atualizações
  const useSocialNetworksUpdate = (callback: () => void) => {
    useEffect(() => {
      const handleUpdate = (event: CustomEvent) => {
        if (event.detail.userId === user?.id) {
          callback();
        }
      };

      window.addEventListener(USER_SOCIAL_NETWORKS_UPDATED, handleUpdate as EventListener);
      return () => {
        window.removeEventListener(USER_SOCIAL_NETWORKS_UPDATED, handleUpdate as EventListener);
      };
    }, [user?.id, callback]);
  };

  useEffect(() => {
    if (user?.id) {
      fetchNetworkTypes();
      fetchSocialNetworks();
    }
  }, [user?.id, fetchNetworkTypes, fetchSocialNetworks]);

  return {
    socialNetworks,
    networkTypes,
    loading,
    error,
    upsertSocialNetwork,
    deleteSocialNetwork,
    getUsernameByType,
    useSocialNetworksUpdate,
    refreshSocialNetworks: fetchSocialNetworks,
  };
};
